import { lazy, Suspense } from 'react';
import { Building2, CalendarRange, ClipboardList, Package, Sparkles, Truck, UserRound } from 'lucide-react';
import { ADMIN_PAGE_ROLES } from '@/config/roles';
import i18n from '@/lib/i18n';
import type { ModuleManifest } from '@/lib/modules';
import en from './i18n/en.json';
import es from './i18n/es.json';
import sr from './i18n/sr.json';
import { MiloPanel } from './milo/MiloPanel';
import { SimulationPanel } from './simulation/SimulationPanel';
import { DRIVER_ROLES, FLEET_STAFF_ROLES } from './roles';

// The module's own strings, merged into the app's translation namespace
for (const [language, bundle] of [
  ['en', en],
  ['es', es],
  ['sr', sr],
] as const) {
  i18n.addResourceBundle(language, 'translation', bundle, true, true);
}

const Planner = lazy(() => import('./pages/Planner'));
const Loads = lazy(() => import('./pages/Loads'));
const Trucks = lazy(() => import('./pages/Trucks'));
const Drivers = lazy(() => import('./pages/Drivers'));
const Customers = lazy(() => import('./pages/Customers'));
const MyLoads = lazy(() => import('./pages/MyLoads'));
const MiloAdmin = lazy(() => import('./milo/MiloAdmin'));
const MiloConversationView = lazy(() => import('./milo/MiloConversationView'));
const LiveBoard = lazy(() => import('./tracking/LiveBoard'));

const fleet: ModuleManifest = {
  routes: [
    { path: 'planner', element: <Planner />, roles: [...FLEET_STAFF_ROLES] },
    { path: 'loads', element: <Loads />, roles: [...FLEET_STAFF_ROLES] },
    { path: 'trucks', element: <Trucks />, roles: [...FLEET_STAFF_ROLES] },
    { path: 'drivers', element: <Drivers />, roles: [...FLEET_STAFF_ROLES] },
    { path: 'customers', element: <Customers />, roles: [...FLEET_STAFF_ROLES] },
    { path: 'milo', element: <MiloAdmin />, roles: [...ADMIN_PAGE_ROLES] },
    { path: 'milo/conversations/:id', element: <MiloConversationView />, roles: [...ADMIN_PAGE_ROLES] },
    { path: 'my-loads', element: <MyLoads />, roles: [...DRIVER_ROLES] },
  ],
  navItems: [
    {
      path: '/planner',
      labelKey: 'fleet.planner.nav',
      defaultLabel: 'Planner',
      icon: CalendarRange,
      roles: [...FLEET_STAFF_ROLES],
      qaId: 'nav-planner',
    },
    {
      path: '/loads',
      labelKey: 'fleet.loads.nav',
      defaultLabel: 'Loads',
      icon: Package,
      roles: [...FLEET_STAFF_ROLES],
      qaId: 'nav-loads',
    },
    {
      path: '/trucks',
      labelKey: 'fleet.nav',
      defaultLabel: 'Trucks',
      icon: Truck,
      roles: [...FLEET_STAFF_ROLES],
      qaId: 'nav-trucks',
    },
    {
      path: '/drivers',
      labelKey: 'fleet.drivers.nav',
      defaultLabel: 'Drivers',
      icon: UserRound,
      roles: [...FLEET_STAFF_ROLES],
      qaId: 'nav-drivers',
    },
    {
      path: '/customers',
      labelKey: 'fleet.customers.nav',
      defaultLabel: 'Customers',
      icon: Building2,
      roles: [...FLEET_STAFF_ROLES],
      qaId: 'nav-customers',
    },
    {
      path: '/milo',
      labelKey: 'fleet.milo.nav',
      defaultLabel: 'AI',
      icon: Sparkles,
      roles: [...ADMIN_PAGE_ROLES],
      qaId: 'nav-milo',
    },
    {
      path: '/my-loads',
      labelKey: 'fleet.myLoads.nav',
      defaultLabel: 'My loads',
      icon: ClipboardList,
      roles: [...DRIVER_ROLES],
      // Drivers' own page: admins and dispatchers see loads on the Planner and Loads, not here
      ownRolesOnly: true,
      qaId: 'nav-my-loads',
    },
  ],
  // Damis AI's button and panel, over every page, for fleet staff; the simulation's launcher for the super admin only (no roles)
  overlays: [
    { element: <MiloPanel />, roles: [...FLEET_STAFF_ROLES] },
    { element: <SimulationPanel />, roles: [] },
  ],
  // Uživo on Početna: the turas going on now
  homeWidgets: [
    {
      element: (
        <Suspense fallback={null}>
          <LiveBoard />
        </Suspense>
      ),
      roles: [...FLEET_STAFF_ROLES],
    },
  ],
};

export default fleet;
